import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import Button from '@orn/shared-web/components/Button/Button';
import { OTPInput } from '@orn/shared-web/components/Input';
import * as React from 'react';

import useAuthApi from '../../../hooks/useAuthApi';
import useCountdown from '../../../hooks/useCountdown';
import { OnboardingTitle } from '../../OnboardingLayout';
import { RESEND_COUNTDOWN_SECONDS } from '../types';

interface IPasswordResetSmsOtpStepProps {
  email: string;
  emailCode: string;
  newPassword: string;
  phoneLast4?: string;
  onSuccess: () => void;
  onBack: () => void;
}

const PasswordResetSmsOtpStep = ({
  email,
  emailCode,
  newPassword,
  phoneLast4,
  onSuccess,
  onBack,
}: IPasswordResetSmsOtpStepProps) => {
  const authApi = useAuthApi();
  const countdown = useCountdown();
  const [otpValue, setOtpValue] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();

  // Start countdown on mount (SMS was just sent)
  React.useEffect(() => {
    countdown.start(RESEND_COUNTDOWN_SECONDS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerify = React.useCallback(
    async (code: string) => {
      setIsSubmitting(true);
      setErrorMessage(undefined);

      const result = await authApi.resetPassword({
        email,
        emailCode,
        smsCode: code,
        newPassword,
      });

      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === ApiErrorCode.AUTH_INVALID_SMS_OTP) {
          setErrorMessage('Invalid or expired SMS code. Please try again.');
        } else if (result.error.code === ApiErrorCode.AUTH_INVALID_OTP) {
          setErrorMessage('Your email code has expired. Please start over.');
        } else {
          setErrorMessage(result.error.error);
        }
        setOtpValue('');
        return;
      }

      onSuccess();
    },
    [authApi, email, emailCode, newPassword, onSuccess]
  );

  const handleResend = React.useCallback(async () => {
    setErrorMessage(undefined);
    const result = await authApi.sendPasswordResetSms({ email, code: emailCode });
    if (result.ok) {
      countdown.start(RESEND_COUNTDOWN_SECONDS);
    } else {
      setErrorMessage(result.error.error);
    }
  }, [authApi, email, emailCode, countdown]);

  const handleComplete = React.useCallback(
    (code: string) => {
      void handleVerify(code);
    },
    [handleVerify]
  );

  return (
    <>
      <OnboardingTitle
        title="Check your phone"
        onBack={onBack}
        subtitle={
          phoneLast4
            ? `We sent a 6-digit code to your phone ending in ${phoneLast4}. Enter it below.`
            : 'We sent a 6-digit code to your phone. Enter it below.'
        }
      />

      <div className="flex w-full flex-col items-center gap-4">
        <OTPInput
          value={otpValue}
          onChange={setOtpValue}
          onComplete={handleComplete}
          isInvalid={Boolean(errorMessage)}
          errorMessage={errorMessage}
          isDisabled={isSubmitting}
          autoFocus
        />

        <div className="flex items-center gap-1">
          {countdown.isActive ? (
            <span className="text-tertiary text-sm">Resend code in {countdown.secondsRemaining}s</span>
          ) : (
            <Button color="link-color" size="sm" onClick={handleResend}>
              Resend code
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

PasswordResetSmsOtpStep.displayName = 'PasswordResetSmsOtpStep';

export default PasswordResetSmsOtpStep;
